import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import ConfirmationModal from 'common/components/confirmation-modal';
import DropdownMenu from 'common/components/dropdown-menu';
import SvgSymbol from 'common/components/svg-symbol';
import {t} from 'common/utils/translation-util';
import './index.scss';

// NOTE: this component uses window.settings stores.  When using this component,
// make sure that the appropriate store variables are available by including
// reports/dashboard_urls.js into a script tag on the template.
export default class DashboardActionsMenu extends React.Component {
  // Extra props passed will be passed along to the DropdownMenu component
  static propTypes = {
    dashboard: PropTypes.object.isRequired,
    showActionEdit: PropTypes.bool,
    usedInAStoryboard: PropTypes.bool,
    userCanEditDashboard: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    // Pass false when used on the edit page, as that would link to the current page
    showActionEdit: true,
    usedInAStoryboard: false,
  };

  constructor(props) {
    super(props);

    this.urls = {
      actionView: window.settings.urls.actionView.replace(
        0,
        props.dashboard.uuid,
      ),
      actionEdit: window.settings.urls.actionEdit.replace(
        0,
        props.dashboard.id,
      ),
      actionExportCsv: window.settings.urls.actionExportCsv.replace(
        0,
        props.dashboard.id,
      ),
      actionExportPdf: window.settings.urls.actionExportPdf.replace(
        0,
        props.dashboard.id,
      ),
      actionExportPng: window.settings.urls.actionExportPng.replace(
        0,
        props.dashboard.id,
      ),
      actionCopy: window.settings.urls.actionCopy.replace(
        0,
        props.dashboard.id,
      ),
      actionDelete: window.settings.urls.actionDelete.replace(
        0,
        props.dashboard.id,
      ),
    };

    this.showModal = this.showModal.bind(this);
    this.onModalConfirm = this.onModalConfirm.bind(this);
  }

  showModal() {
    this._modal.show();
  }

  render() {
    const {dashboard, userCanEditDashboard, usedInAStoryboard} = this.props;

    const canWrite = window.settings.user.canWrite;
    const canSchedule = window.settings.user.canCreateSchedules;
    const isDefault = dashboard.default;

    const showEmail = canSchedule && !isDefault;

    const showEdit =
      this.props.showActionEdit && !isDefault && userCanEditDashboard;
    const showSettings = canWrite && !isDefault && userCanEditDashboard;

    const showDuplicate = canWrite;

    const showCopyTo = window.settings.user.canCopyDashboards;

    const showDelete = canWrite && !isDefault && userCanEditDashboard;

    const deleteWarning = usedInAStoryboard
      ? t(
          'This dashboard is used in one or more storyboards.\nProceed with caution! This cannot be undone.',
        )
      : t('Proceed with caution! This cannot be undone.');

    return (
      <span>
        <DropdownMenu {...this.props}>
          {showSettings ? (
            <React.Fragment>
              <li>
                <a
                  className={'categorizeDashboard'}
                  data-dashboard-id={this.props.dashboard.id}
                  href={'#'}>
                  <SvgSymbol name={'edit'} />
                  {t('Dashboard settings')}
                </a>
              </li>
              <li className={'DropdownMenuItem--separator'}>
                <hr />
              </li>
            </React.Fragment>
          ) : null}

          {showEdit ? (
            <li>
              <a href={this.urls.actionEdit}>
                <SvgSymbol name={'fullscreen'} />
                {t('Edit dashboard')}
              </a>
            </li>
          ) : null}

          {showDuplicate ? (
            <li>
              <form action={this.urls.actionCopy} method={'POST'}>
                <button type={'submit'}>
                  <SvgSymbol name={'duplicate'} />
                  {t('Duplicate dashboard')}
                </button>
                <input
                  name={'csrfmiddlewaretoken'}
                  type={'hidden'}
                  value={window.settings.csrf}
                />
              </form>
            </li>
          ) : null}

          {showCopyTo ? (
            <li>
              <a
                className={'copyToOrg'}
                data-dashboard_id={this.props.dashboard.id}
                href={'#'}>
                <SvgSymbol name={'duplicate'} />
                {t('Copy to...')}
              </a>
            </li>
          ) : null}

          {showEdit || showDuplicate || showCopyTo ? (
            <li className={'DropdownMenuItem--separator'}>
              <hr />
            </li>
          ) : null}

          {showEmail ? (
            <li>
              <a
                className={'emailDelivery'}
                data-dashboard-id={this.props.dashboard.id}
                data-dashboard-name={this.props.dashboard.name}
                href={'#'}>
                <SvgSymbol name={'email-card'} />
                {t('Email dashboard')}
              </a>
            </li>
          ) : null}

          <li>
            <a href={this.urls.actionExportPng}>
              <SvgSymbol name={'download-image'} />
              {t('Download image')}
            </a>
          </li>

          <li>
            <a href={this.urls.actionExportPdf}>
              <SvgSymbol name={'download-pdf'} />
              {t('Download PDF')}
            </a>
          </li>

          <li>
            <a href={this.urls.actionExportCsv}>
              <SvgSymbol name={'download-csv'} />
              {t('Download CSV')}
            </a>
          </li>

          <li>
            <a href={this.urls.actionView} rel='noreferrer' target={'_blank'}>
              <SvgSymbol name={'fullscreen'} />
              {t('View fullscreen')}
            </a>
          </li>

          <li className={'DropdownMenuItem--separator'}>
            <hr />
          </li>

          {showDelete ? (
            <li>
              <form
                action={this.urls.actionDelete}
                method={'POST'}
                ref={(form) => (this._deleteForm = form)}>
                <input
                  name={'csrfmiddlewaretoken'}
                  type={'hidden'}
                  value={window.settings.csrf}
                />
              </form>
              <a href={'#'} onClick={this.showModal}>
                <SvgSymbol name={'delete'} />
                {t('Delete dashboard')}
              </a>
            </li>
          ) : null}
        </DropdownMenu>
        <ConfirmationModal
          body={deleteWarning}
          buttonProps={{
            children: t('Delete'),
            type: 'red',
            withIcon: true,
          }}
          header={t('Delete this dashboard')}
          onConfirm={this.onModalConfirm}
          ref={(confirmationModal) => (this._modal = confirmationModal)}
        />
      </span>
    );
  }

  onModalConfirm() {
    this._deleteForm.submit();
  }
}
